import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import httpInstances from '@/api/axiosInstance';
import type { FactsheetDetails } from '@/features/Factsheet/types';
import { parseBackendData } from '@/utils/parseBackendData';

import type { UseQueryOptions } from '@tanstack/react-query';

// Query Keys - centralized for cache management
export const factsheetQueryKeys = {
  all: ['factsheet'] as const,
  details: (code: string) => ['factsheet', 'details', code] as const,
  mapImage: (location: string, zoom: string) => ['factsheet', 'map', location, zoom] as const
};

/**
 * Fetches factsheet details by code from the API.
 */
async function fetchFactsheetDetails(code: string): Promise<FactsheetDetails> {
  const { data } = await httpInstances.axiosInternalInstance.get(
    `/factsheets/details?code=${encodeURIComponent(code)}`
  );

  const factsheets: FactsheetDetails[] = parseBackendData(data);

  if (!factsheets || factsheets.length === 0) {
    throw new Error('Factsheet not found');
  }

  return factsheets[0];
}

/**
 * Hook to fetch factsheet details using React Query.
 * Provides automatic caching, background refetching, and error handling.
 */
export function useFactsheetDetails(
  code: string,
  options?: Omit<UseQueryOptions<FactsheetDetails, AxiosError>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: factsheetQueryKeys.details(code),
    queryFn: () => fetchFactsheetDetails(code),
    enabled: Boolean(code) && code.length > 0,
    retry: (failureCount, error) => {
      // Don't retry on 404 errors
      if (error instanceof AxiosError && error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    refetchOnWindowFocus: false,
    ...options
  });
}

/**
 * Fetches map image blob for a factsheet location.
 */
async function fetchMapImage(location: string, zoom: string): Promise<string> {
  const locationUri = `/factsheets/config/location?location=${encodeURI(location.replace(' ', ','))}&zoom=${zoom}&dark=true`;

  const response = await httpInstances.axiosInternalInstance.get(locationUri, {
    responseType: 'blob'
  });

  if (response.status !== 200) {
    throw new Error('Unable to get map image');
  }

  return URL.createObjectURL(response.data);
}

/**
 * Hook to fetch factsheet map image using React Query.
 */
export function useFactsheetMapImage(
  location: string | undefined,
  zoom = '10',
  options?: Omit<UseQueryOptions<string, AxiosError>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: factsheetQueryKeys.mapImage(location ?? '', zoom),
    queryFn: () => fetchMapImage(location!, zoom),
    enabled: Boolean(location) && location!.length > 0,
    retry: false,
    staleTime: Infinity, // Map images don't change
    refetchOnWindowFocus: false,
    ...options
  });
}

/**
 * Fetches a flowchart image from the datalake.
 */
async function fetchFlowchartImage(fileId: string): Promise<string> {
  const { default: downloadFileFromDatalake } = await import('@/utils/downloadFileFromDatalake');
  const response = await downloadFileFromDatalake(fileId);
  return URL.createObjectURL(response?.data);
}

/**
 * Hook to fetch flowchart image using React Query.
 */
export function useFlowchartImage(
  fileId: string | undefined,
  options?: Omit<UseQueryOptions<string, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['factsheet', 'flowchart', fileId] as const,
    queryFn: () => fetchFlowchartImage(fileId!),
    enabled: Boolean(fileId) && fileId !== 'false',
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    ...options
  });
}
